import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import UtilityHelper from "../../../service/utilities/UtilityHelper";
import LoginProvider from "./LoginProvider";
import Loader from "../../../widget/common/ui/Loader";
import { useDispatch } from "react-redux";
import {
  addModal,
  removeAllModal,
} from "../../../libraries/state_reducers/widget/ModalsReducer";
import { setBranchListScreenName } from "../../../libraries/state_reducers/branch/BranchListReducer";
import ModalKeys from "../../../const/ModalKeys";
import AppTranslation from "../../../service/app_translation/AppTranslation";
import { routeName as routeNameSignupVerification } from "../../../screens/onboarding/signup/account_verification/SignupAccountVerificationScreen";
import SessionStorage from "../../../service/storage/SessionStorage";
import StorageKeys from "../../../const/StorageKeys";
import CacheStorage from "../../../service/storage/CacheStorage";
import { phoneMobileDefault } from "../../../const/MobileConstant";
import { CountryCodeList } from "../../../model/country_code/CountryCodeModel";
import { SocialSignInResponseType } from "../../../libraries/types_interface/social/LoginSocialConnectTypes";
import { ResponsiveDeviceType } from "../../../widget/layouts/ScreenMedia";
import AppConfigService from "../../../service/app_config/AppConfigService";
import { routeName as routeNameLogin } from "../../../screens/onboarding/login/LoginScreen";

type LoginContextType = {
  credential: CredentialType;
  credentialErrors: CredentialErrorType;
  credentialValidate: boolean;

  handleOnChange: (name: string, value: string) => void;
  handleClickLogin: () => void;
  handleClickContinueGuest: () => void;

  socialGoogleSuccess: (detail: SocialSignInResponseType) => void;

  tabName: "email" | "phone";
  handleChangeTab: (name: "email" | "phone") => void;

  applyCountryCode: (countryCodeDetail: CountryCodeList) => void;
};

type CredentialType = {
  email: string;
  password: string;
  phoneField: string;
  phoneCodeField: string;
  phoneCodeIdField: string;
};

type CredentialErrorType = {
  email: boolean;
  password: boolean;
  phoneField: boolean;
  phoneCodeField: boolean;
  phoneCodeIdField: boolean;
};

const LoginContext = createContext<LoginContextType | undefined>(undefined);

export const LoginContextProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch();
  const type: number = ResponsiveDeviceType();

  /* const countryCodeDetail = useSelector(
    (state: RootState) => state.countryCode.countryCodeDetail
  ); */

  const credentialErrorDefault: CredentialErrorType = {
    email: false,
    password: false,
    phoneField: false,
    phoneCodeField: true,
    phoneCodeIdField: true,
  };

  // 0-none, 1-apple, 2-google, 3-huawei
  const socailFlagRef = useRef<"0" | "1" | "2" | "3">("0");

  const [tabName, setTabName] = useState<"email" | "phone">("email");
  const [credentialValidate, setCredentialValidate] = useState<boolean>(false);
  const [credential, setCredential] = useState<CredentialType>({
    email: "",
    password: "",
    phoneField: "",
    phoneCodeField: phoneMobileDefault.phone_code,
    phoneCodeIdField: phoneMobileDefault.phone_code_id,
  });
  const [credentialErrors, setCredentialErrors] = useState<CredentialErrorType>(
    credentialErrorDefault
  );

  const handleOnChange = (fieldName: string, fieldValue: string) => {
    fieldName = fieldName.toLowerCase();

    let credentialTemp: CredentialType = credential;

    if (fieldName === `email`) {
      credentialTemp = { ...credentialTemp, email: fieldValue };
    } else if (fieldName === `password`) {
      credentialTemp = { ...credentialTemp, password: fieldValue };
    } else if (fieldName === `phone_number`) {
      credentialTemp = { ...credentialTemp, phoneField: fieldValue };
    }

    setCredential({ ...credentialTemp });
  };

  const applyCountryCode = (countryCodeDetail: CountryCodeList) => {
    if (countryCodeDetail != null) {
      let credentialTemp: CredentialType = credential;
      credentialTemp = {
        ...credentialTemp,
        phoneCodeField: countryCodeDetail?.country_phonecode!,
        phoneCodeIdField: countryCodeDetail?.country_id!,
      };
      setCredential({ ...credentialTemp });
    }
  };

  const validateErrors = (): boolean => {
    let errorCounter = 0;

    let credentialTemp: CredentialType = credential;
    let credentialErrosTemp: CredentialErrorType = credentialErrorDefault;

    if (
      (UtilityHelper.isEmpty(credentialTemp.email) ||
        !UtilityHelper.isEmail(credentialTemp.email)) &&
      tabName === "email"
    ) {
      errorCounter++;
      credentialErrosTemp = { ...credentialErrosTemp, email: true };
    }
    if (
      (UtilityHelper.isEmpty(credentialTemp.phoneField) ||
        !UtilityHelper.phoneNumberValid(credentialTemp.phoneField) ||
        UtilityHelper.isEmpty(credentialTemp.phoneCodeIdField)) &&
      tabName === "phone"
    ) {
      errorCounter++;
      credentialErrosTemp = { ...credentialErrosTemp, phoneField: true };
    }

    if (UtilityHelper.isEmpty(credentialTemp.password)) {
      errorCounter++;
      credentialErrosTemp = { ...credentialErrosTemp, password: true };
    }

    setCredentialErrors({ ...credentialErrosTemp });
    setCredentialValidate(errorCounter === 0 ? true : false);

    return errorCounter === 0 ? true : false;
  };

  const handleClickLogin = async () => {
    if (validateErrors()) {
      socailFlagRef.current = "0";

      let phone: string = "";
      if (tabName === "phone") {
        phone = `${credential.phoneCodeField.trim()}${credential.phoneField.trim()}`;
      }

      Loader.modal();
      const statusResponse: null | true | "no_default_branch" =
        await LoginProvider.signIn(
          credential.email,
          phone,
          credential.password,
          tabName
        );
      Loader.modal(false);

      console.log("statusResponse", statusResponse);
      if (statusResponse === "no_default_branch") {
        defaultBranchSelection();
      }
    }
  };

  const defaultBranchSelection = () => {
    dispatch(
      setBranchListScreenName({
        screenName: routeNameSignupVerification,
      })
    );

    divisionBranchSelection();
  };

  const handleClickContinueGuest = () => {
    dispatch(
      setBranchListScreenName({
        screenName: routeNameLogin, //routeNameHomeGuest
      })
    );

    divisionBranchSelection();
  };

  const divisionBranchSelection = () => {
    dispatch(removeAllModal());

    const companyGroupType: string =
      AppConfigService.get("company_group_type") ?? "0";

    if (companyGroupType === "1") {
      dispatch(
        addModal({
          id: ModalKeys.list_choose_division,
          title: type === 3 ? "choose_division" : "divisions",
          screenMobileView: type !== 3,
        })
      );
    } else {
      dispatch(
        addModal({
          id: ModalKeys.list_choose_branch,
          title: AppTranslation.text("choose_preferred_branch"),
          screenMobileView: type !== 3,
          static: false,
        })
      );
    }
  };

  const handleChangeTab = (name: "email" | "phone") => {
    setTabName(name);
  };

  // SOCIAL LOGIN
  const socialGoogleSuccess = (detail: SocialSignInResponseType) => {
    console.log(detail);
    socailFlagRef.current = "2";
    loginBySocial(detail);
  };

  const loginBySocial = async (detail: SocialSignInResponseType) => {
    if (Number(socailFlagRef.current) > 0) {
      Loader.modal();
      const statusResponse: null | true | "no_default_branch" =
        await LoginProvider.signInBySocial({
          socialType: socailFlagRef.current,
          socialEmail: detail.email,
          socialId: detail.id,
          socialToken:
            socailFlagRef.current === "1" ? detail.token ?? "" : detail.email,
        });
      Loader.modal(false);

      if (statusResponse === "no_default_branch") {
        defaultBranchSelection();
      }
    }
  };

  useEffect(() => {
    // DELETE GUEST
    console.log(`DELETE GUEST SESSION`);

    CacheStorage.deleteValue(StorageKeys.branch_id);
    CacheStorage.deleteValue(StorageKeys.login_status);

    CacheStorage.deleteValue(StorageKeys.division_id);
    CacheStorage.deleteValue(StorageKeys.division_title);

    SessionStorage.deleteValue(StorageKeys.division_id);
    SessionStorage.deleteValue(StorageKeys.division_title);

    // FORGOT PASSWORD
    SessionStorage.deleteValue(StorageKeys.signup_verification_detail);
    SessionStorage.deleteValue(StorageKeys.recover_password_detail);

    const guestStatus: string =
      SessionStorage.getValue(StorageKeys.guest_status) || "0";
    SessionStorage.deleteValue(StorageKeys.guest_status);

    LoginProvider.setupDetaults();

    if (guestStatus === "1") {
      LoginProvider.resetTheme();
      window.location.reload();
    }
  }, []);

  return (
    <LoginContext.Provider
      value={{
        credential,
        handleOnChange,
        handleClickLogin,
        handleClickContinueGuest,
        credentialErrors,
        credentialValidate,
        socialGoogleSuccess,

        tabName,
        handleChangeTab,

        applyCountryCode,
      }}
    >
      {children}
    </LoginContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const LoginContextImport = () => {
  const context = useContext(LoginContext);
  if (!context) {
    throw new Error("useMyContext must be used within a LoginContextProvider");
  }
  return context;
};
